import React, { useEffect, useState } from "react";
import { GetMachineServiceType } from "../../../models/_tickets";
import {
  GetBlendersType,
  GetBoardsType,
  GetDiagnosisType,
} from "../../../models/_publicCall";
import { MACHINE_SERVICES } from "../../../../_metronic/helpers/enums";
import Select from "react-select";
import { Toaster } from "react-hot-toast";
import { SlideshowLightbox } from "lightbox.js-react";
import { SiteVisitationMachine, useSiteVisitation, SiteVisitationSteps } from "../../../providers/SiteVisitationProvider";
import Modal from "react-responsive-modal";
import machineListImage from "../../../../images/TicketInfo.png";

const TicketsInfo = (props: {
  selectedMachine: { [machineId: string]: SiteVisitationMachine };
  diagnosis: GetDiagnosisType[];
  machineServicesDB: GetMachineServiceType[];
  blendersDB: GetBlendersType[];
  boardsDB: GetBoardsType[];
  setCurrentStep: (step: number) => void;
  jobId: any;
}) => {
  const {
    jobId,
    selectedMachine,
    diagnosis,
    machineServicesDB,
    blendersDB,
    boardsDB,
    setCurrentStep,
    
  } = props;
  const { validateTickets, setTicketsForJob, selectedMachineTickets, updateTicketInStore, logJobMachineDetail, setSelectedMachine, fetchSiteVisitJobOrderMachineDetail } = useSiteVisitation();

  const [validationErrors, setValidationErrors] = useState<{ [ticketId: string]: string }>({});
  const [collapsedDiagnosis, setCollapsedDiagnosis] = useState<{ [ticketId: string]: boolean }>({});
  const [collapsedServices, setCollapsedServices] = useState<{ [ticketId: string]: boolean }>({});
  const [showMachineInfoModal, setShowMachineInfoModal] = useState(false);
  const [diagnosisSearch, setDiagnosisSearch] = useState<{ [ticketId: string]: string }>({});
  const [serviceSearch, setServiceSearch] = useState<{ [ticketId: string]: string }>({});

  useEffect(() => {
    if (!selectedMachine?.id ||  logJobMachineDetail) return;
    setSelectedMachine(String(selectedMachine.id), jobId);
    fetchSiteVisitJobOrderMachineDetail(String(selectedMachine.id));
  }
  , [selectedMachine, logJobMachineDetail]);

  useEffect(() => {
    if (!selectedMachineTickets) return;

    setCollapsedDiagnosis((prev) => {
      const newState = { ...prev };
      selectedMachineTickets.forEach((ticket: any) => {
      if (!(ticket.id in newState)) {
        newState[ticket.id] = false;
      }
      });
      Object.keys(newState).forEach((id) => {
      if (!selectedMachineTickets.some((ticket: any) => ticket.id === id)) {
        delete newState[id];
      }
      });
      return newState;
    });
    setCollapsedServices((prev) => {
      const newState = { ...prev };
      selectedMachineTickets.forEach((ticket: any) => {
      if (!(ticket.id in newState)) {
        newState[ticket.id] = false; 
      }
      });
      Object.keys(newState).forEach((id) => {
      if (!selectedMachineTickets.some((ticket: any) => ticket.id === id)) {
        delete newState[id];
      }
      });
      return newState;
    });
  }, [selectedMachineTickets]);

  // Fetch machine detail if needed
  useEffect(() => {
    if (!selectedMachine || !logJobMachineDetail) return;

    const storeTickets = logJobMachineDetail.tickets || [];
    setTicketsForJob(jobId, String(selectedMachine.id), storeTickets);
  }, [selectedMachine, logJobMachineDetail]);

  // Handlers for checkboxes and inputs
  const handleCheckboxChange = (ticketId: string, diagnosisId: string) => {
    const ticket = selectedMachineTickets.find((t: any) => t.id === ticketId);
    const prevArr = ticket?.diagnosis || [];
    const updatedArr = prevArr.includes(diagnosisId)
      ? prevArr.filter((id: string) => id !== diagnosisId)
      : [...prevArr, diagnosisId];
    updateTicketInStore(jobId, String(selectedMachine.id), ticketId, { diagnosis: updatedArr });
  };

  const handleOtherDiagnosisChange = (ticketId: string, value: string) => {
    updateTicketInStore(jobId, String(selectedMachine.id), ticketId, { diagnosisOther: value });
  };

  const handleMachineServiceChange = (ticketId: string, serviceId: string) => {
    const ticket = selectedMachineTickets.find((t: any) => t.id === ticketId);
    const prevArr = ticket?.machineServicesDone || [];
    const updatedArr = prevArr.includes(serviceId)
      ? prevArr.filter((id: string) => id !== serviceId)
      : [...prevArr, serviceId];
    updateTicketInStore(jobId, String(selectedMachine.id), ticketId, { machineServicesDone: updatedArr });
  };

  const handleOtherServiceChange = (ticketId: string, value: string) => {
    updateTicketInStore(jobId, String(selectedMachine.id), ticketId, { machineServicesDoneOther: value });
  };

  const handleBlenderChange = (ticketId: string, value: string) => {
    updateTicketInStore(jobId, String(selectedMachine.id), ticketId, { replacedBlender: value });
  };

  const handleBoardChange = (ticketId: string, value: string) => {
    updateTicketInStore(jobId, String(selectedMachine.id), ticketId, { replacedBoard: value });
  };

  const handleKeepTicketOpenChange = (ticketId: string, value: boolean) => {
    updateTicketInStore(jobId, String(selectedMachine.id), ticketId, { keepTicketOpen: value });
  };

  // Collapse toggles
  const toggleCollapse = (ticketId: string) =>
    setCollapsedDiagnosis((prev) => ({ ...prev, [ticketId]: !prev[ticketId] }));
  const toggleCollapseServices = (ticketId: string) =>
    setCollapsedServices((prev) => ({ ...prev, [ticketId]: !prev[ticketId] }));

  // Validation and finish
  const handleFinishTickets = () => {
    const ticketsArr = selectedMachineTickets;
    const errors = validateTickets(
      ticketsArr,
      Object.fromEntries(ticketsArr.map((t: any) => [t.id, t.diagnosis || []])),
      Object.fromEntries(ticketsArr.map((t: any) => [t.id, t.diagnosisOther || ""])),
      Object.fromEntries(ticketsArr.map((t: any) => [t.id, t.machineServicesDone || []])),
      Object.fromEntries(ticketsArr.map((t: any) => [t.id, t.machineServicesDoneOther || ""])),
      Object.fromEntries(ticketsArr.map((t: any) => [t.id, t.replacedBlender || ""])),
      Object.fromEntries(ticketsArr.map((t: any) => [t.id, t.replacedBoard || ""]))
    );
    setValidationErrors(errors);
    if (Object.keys(errors).length === 0) {
      setCurrentStep(SiteVisitationSteps.MACHINE_INFO);
      console.log("All validations passed. Proceeding...");
    } else {
      console.error("Validation failed. Check errors.");
    }
  };

  // Getters for current ticket state
  const getTicketById = (ticketId: string) => selectedMachineTickets.find((t: any) => t.id === ticketId) || {};
  const getSelectedDiagnosis = (ticketId: string) => getTicketById(ticketId).diagnosis || [];
  const getDiagnosisOther = (ticketId: string) => getTicketById(ticketId).diagnosisOther || "";
  const getSelectedMachineServices = (ticketId: string) => getTicketById(ticketId).machineServicesDone || [];
  const getMachineServicesOther = (ticketId: string) => getTicketById(ticketId).machineServicesDoneOther || "";
  const getReplacedBlender = (ticketId: string) => getTicketById(ticketId).replacedBlender || "";
  const getReplacedBoard = (ticketId: string) => getTicketById(ticketId).replacedBoard || "";
  const getKeepTicketOpen = (ticketId: string) => getTicketById(ticketId).keepTicketOpen || false;
  const formatTicketType = (type: string) =>
    type
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  

  return (
    <>
      <div className="wo-form-section mt-8">
        <header className="header">
          <h2>List of Tickets</h2>
        </header>
        <div className="flex justify-between items-center m-4">
          <div className="left-icon mt-4" onClick={() => setCurrentStep(SiteVisitationSteps.MACHINE_LIST)}>
            <i className="fa fa-arrow-left " aria-hidden="true"></i>
          </div>
          <div className="icon-container mt-4" onClick={() => setShowMachineInfoModal(true)}>
            <i className="fa fa-question-circle " aria-hidden="true"></i>
          </div>
        </div>
        <div className="machine-wrapper">
          <div className="mb-4 text-sm font-medium">
            <p>
              <span className="font-semibold">Machine Name:</span>{" "}
              {String(selectedMachine?.name)}
            </p>
            <p>
              <span className="font-semibold">Serial Number:</span>{" "}
              {String(selectedMachine?.serialNumber)}
            </p>
          </div>
          <div className="row mb-4">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <button
                className="btn btn-primary w-100"
                type="button"
                onClick={() => setCurrentStep(SiteVisitationSteps.CREATE_TICKET)}
              >
                Create Ticket <i className="fa fa-plus ms-2" aria-hidden="true"></i>
              </button>
            </div>
            <div className="col-12 col-md-6">
              <button
                className="btn btn-primary w-100"
                type="button"
                onClick={() => setCurrentStep(SiteVisitationSteps.REPLACE_MACHINE)}
              >
                Replace Machine <i className="fa fa-refresh ms-2" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          {selectedMachineTickets?.length > 0 &&
            selectedMachineTickets.map((ticket: any, index: number) =>(
              <div className="ticket-card" key={ticket.id}>
                {/* Ticket Type */}
                <div className="row mb-3">
                  <div className="col-md-6 fw-bold">Ticket Type:</div>
                  <div className="col-md-6">{formatTicketType(ticket.ticketType)}</div>
                </div>
                {/* Issues Reported */}
                <div className="row mb-3">
                  <div className="col-md-6 fw-bold">Issue Reported:</div>
                  <div className="col-md-6">
                    <ul className="mb-0">
                      {ticket?.issuesReported?.map((issue: any, idx: number) => (
                        <li key={idx} className="fs-6">{issue.name}</li>
                      ))}
                      {ticket?.issueReportedOtherInput && (
                        <li className="fs-6">{ticket.issueReportedOtherInput}</li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Recommended Services */}
                <div className="row mb-3">
                  <div className="col-md-6 fw-bold">Recommended Services:</div>
                  <div className="col-md-6">
                    <ul className="mb-0">
                      {ticket?.machineServices?.map((service: any, idx: number) => (
                        <li key={idx} className="fs-6">{service.name}</li>
                      ))}
                      {ticket?.machineServiceOtherInput && (
                        <li className="fs-6">{ticket.machineServiceOtherInput}</li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Recommended Operations */}
                <div className="row mb-3">
                  <div className="col-md-6 fw-bold">Recommended Operations:</div>
                  <div className="col-md-6">
                    <ul className="mb-0">
                      {ticket?.operations?.map((operation: any, idx: number) => (
                        <li key={idx} className="fs-6">{operation.name}</li>
                      ))}
                      {ticket?.operationOtherInput && (
                        <li className="fs-6">{ticket.operationOtherInput}</li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Images/Videos */}
                {ticket.media?.length > 0 && (
                  <div className="row mb-3">
                    <div className="col-md-6 fw-bold">Images:</div>
                    <div className="col-md-6">
                      <div className="row g-3">
                        {ticket.media.map((image: any, idx: number) => (
                          <div key={idx} className="col-12 col-md-6">
                            {["image/png", "image/jpeg", "image/webp", "image/jpg"].includes(image["type"]) ? (
                              <SlideshowLightbox className="container grid grid-cols-3 gap-2 mx-auto">
                                <img
                                  src={image["url"]}
                                  key={image["url"]}
                                  alt="Images"
                                  width="250"
                                  className="img-view img-fluid position-relative overflow-hidden rounded-1 p-2 bg-light mt-2"
                                />
                              </SlideshowLightbox>
                            ) : (
                              <video
                                controls
                                src={image["url"]}
                                className="img-view img-fluid position-relative overflow-hidden rounded-1 p-2 bg-light mt-2"
                                width="250"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Comments */}
                <hr className="mb-4" />
                <div className="row mb-3">
                  <div className="col-md-6 fw-bold">Comments:</div>
                  <div className="col-md-6">{ticket.comment}</div>
                </div>

                {/* Diagnosis */}
                <div className="gap-2 mt-4">
                  <hr className="my-4" />
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <label htmlFor="diagnosisId" className="wo-label mb-0">Diagnosis:</label>
                    <button
                      type="button"
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg border-0"
                      onClick={() => toggleCollapse(ticket.id)}
                    >
                      {collapsedDiagnosis[ticket.id] ? (
                        <i className="bi bi-chevron-down fs-2"></i>
                      ) : (
                        <i className="bi bi-chevron-up fs-2"></i>
                      )}
                    </button>
                  </div>
                  <div className={`ms-2 mt-2 ${collapsedDiagnosis[ticket.id] ? "d-none" : "d-block"}`}>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Search diagnosis"
                      value={diagnosisSearch[ticket.id] || ""}
                      onChange={(e) =>
                        setDiagnosisSearch((prev) => ({
                          ...prev,
                          [ticket.id]: e.target.value,
                        }))
                      }
                    />
                    <ul className="list-unstyled">
                      {[...(diagnosisSearch[ticket.id] ? diagnosis.filter((diag) =>
                        diag.name.toLowerCase().includes(diagnosisSearch[ticket.id].toLowerCase())
                      ) : diagnosis), { id: "other", name: "Other" }].map((diag: GetDiagnosisType, index: number) => (
                        <label key={index} className="form-check form-check-sm form-check-custom form-check-solid me-5 d-flex align-items-center ms- mt-1">
                          <input
                            id={`diagnosis-${ticket.id}-${index}`}
                            className="form-check-input mb-2 me-1"
                            type="checkbox"
                            checked={getSelectedDiagnosis(ticket.id).includes(diag.id)}
                            onChange={() => handleCheckboxChange(ticket.id, diag.id)}
                          />
                          <span className="ms-2 mb-1 fs-5 form-check-label" style={{ fontWeight: 'normal' }}>{diag.name}</span>
                        </label>
                      ))}
                      {getSelectedDiagnosis(ticket.id).includes("other") && (
                        <input
                          type="text"
                          className="form-control ms-2"
                          placeholder="Enter Other Diagnosis"
                          value={getDiagnosisOther(ticket.id)}
                          onChange={(e) => handleOtherDiagnosisChange(ticket.id, e.target.value)}
                        />
                      )}
                    </ul>
                  </div>
                </div>

                {/* Machine Services Done */}
                <div className="gap-2 mt-4">
                  <hr className="my-4" />
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <label htmlFor="machineServicesDone" className="wo-label mb-0">Machine Services Done:</label>
                    <button
                      type="button"
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg border-0"
                      onClick={() => toggleCollapseServices(ticket.id)}
                    >
                      {collapsedServices[ticket.id] ? (
                        <i className="bi bi-chevron-down fs-2"></i>
                      ) : (
                        <i className="bi bi-chevron-up fs-2"></i>
                      )}
                    </button>
                  </div>
                  <div className={`mt-2 ${collapsedServices[ticket.id] ? "d-none" : "d-block"}`}>
                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Search service done"
                      value={serviceSearch[ticket.id] || ""}
                      onChange={(e) =>
                        setServiceSearch((prev) => ({
                          ...prev,
                          [ticket.id]: e.target.value,
                        }))
                      }
                    />
                    <ul className="list-unstyled">
                      {(serviceSearch[ticket.id] ? machineServicesDB.filter((service) => service.name.toLowerCase().includes(serviceSearch[ticket.id].toLowerCase()) || service.id === 'other') : machineServicesDB).map((service: GetMachineServiceType, index: number) => (
                        <div key={index} className="d-flex align-items-center ms-2 ">
                          <label className="form-check form-check-sm form-check-custom form-check-solid me-5 d-flex align-items-center">
                            <input
                              id={`machineServicesDone-${ticket.id}-${index}`}
                              className="form-check-input mb-2 me-1"
                              type="checkbox"
                              checked={getSelectedMachineServices(ticket.id).includes(service.id)}
                              onChange={() => handleMachineServiceChange(ticket.id, service.id)}
                            />
                            <span className="ms-2 mb-1 fs-5 form-check-label" style={{ fontWeight: 'normal' }}>{service.name}</span>
                          </label>
                        </div>
                      ))}
                      {getSelectedMachineServices(ticket.id).includes("other") && (
                        <input
                          type="text"
                          className="form-control ms-2"
                          placeholder="Enter service details"
                          value={getMachineServicesOther(ticket.id)}
                          onChange={(e) => handleOtherServiceChange(ticket.id, e.target.value)}
                        />
                      )}
                      {getSelectedMachineServices(ticket.id).includes(MACHINE_SERVICES.REPLACE_BLENDER_ASSEMBLY) && (
                        <div className="">
                          <label className="form-label required fs-4 fw-bold">Blender</label>
                          <Select
                            className="react-select"
                            classNamePrefix="my-react-select"
                            name="blenderId"
                            placeholder=""
                            options={blendersDB.map((value: any) => ({
                              value: value.id,
                              label: value.title,
                            }))}
                            value={{
                              label: blendersDB.find((value: any) => value.id === getReplacedBlender(ticket.id))?.title,
                              value: getReplacedBlender(ticket.id),
                            }}
                            onChange={(e: any) => handleBlenderChange(ticket.id, e.value)}
                          />
                        </div>
                      )}
                      {(getSelectedMachineServices(ticket.id).includes(MACHINE_SERVICES.UPGRADE_PCB) ||
                        getSelectedMachineServices(ticket.id).includes(MACHINE_SERVICES.REPLACE_PCB)) && (
                        <div className="">
                          <label className="form-label required fs-4 fw-bold">Board</label>
                          <Select
                            className="react-select"
                            classNamePrefix="my-react-select"
                            name="boardId"
                            placeholder=""
                            value={{
                              label: boardsDB.find((value: any) => value.id === getReplacedBoard(ticket.id))?.title,
                              value: getReplacedBoard(ticket.id),
                            }}
                            options={boardsDB.map((value: any) => ({
                              value: value.id,
                              label: value.title,
                            }))}
                            onChange={(e: any) => handleBoardChange(ticket.id, e.value)}
                          />
                        </div>
                      )}
                    </ul>
                  </div>
                  <div>
                    <ul className="list-unstyled">
                      <li>
                        <label className="form-check form-check-sm form-check-custom form-check-solid me-5 d-flex align-items-center">
                          <input
                            id={`keep-ticket-open-${ticket.id}`}
                            className="form-check-input mb-2 me-1"
                            type="checkbox"
                            checked={getKeepTicketOpen(ticket.id)}
                            onChange={(e: any) => handleKeepTicketOpenChange(ticket.id, e.target.checked)}
                          />
                          <span className="ms-2 mb-1 fs-5 form-check-label font-semibold" style={{ fontWeight: 'normal' }}>Keep this ticket open</span>
                        </label>
                      </li>
                    </ul>
                  </div>
                </div>
                {validationErrors[ticket.id] && (
                  <div className="text-danger mt-2">{validationErrors[ticket.id]}</div>
                )}
              </div>
            ))}
          <button
            className="btn btn-primary float-end"
            onClick={handleFinishTickets}
            type="button"
          >
            Finished Working On Tickets <i className="fa fa-check-circle ms-2" aria-hidden="true"></i>
          </button>
        </div>
        <Modal
        open={showMachineInfoModal}
        onClose={() => setShowMachineInfoModal(false)}
        center
        classNames={{ root: "content-wrapper" }}
      >
        <div className="modal-body">
          <div className="p-4">
            <div className="m-4">
              <img src={machineListImage} alt="machine" height={500}/>
            </div>
          </div>
        </div>
      </Modal>
        <Toaster
          position="bottom-left"
          reverseOrder={false}
          gutter={8}
          containerClassName=""
          containerStyle={{}}
          toastOptions={{
            className: "",
            duration: 3000,
          }}
        />
      </div>
    </>
  );
};

export default TicketsInfo;